import React, { useCallback, useEffect, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import useBaseService from "../../../../hooks/services/useBaseService";
import useLoader from "../../../../hooks/useLoader";
import useNotification from "../../../../hooks/useNotification";
import useForm from "../../../../hooks/useForm";
import {
  cleanPayload,
  normalizeFormErrors,
  setDefaultFieldValues,
} from "../../../../utils/objectUtils";
import { typeFields } from "../../../../constants/forms/almoxarifado/typeFields";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { entities } from "../../../../constants/entities";
import MainLayout from "../../../../layouts/MainLayout";
import PageHeader from "../../../../components/PageHeader";
import SectionHeader from "../../../../components/forms/SectionHeader";
import SimpleBody from "../../../../components/forms/SimpleBody";
import Form from "../../../../components/forms/Form";
import useUrlFilters from "../../../../hooks/useUrlFilters";
import useQueryWithLoader from "../../../../hooks/useQueryWithLoader";
import useSelectOptions from "../../../../hooks/useSelectOptions";

const EditTypePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const { filters, pagination } = useUrlFilters(
    {
      id: [],
      name: [],
      deleted_at: false,
    },
    "almox_types",
  );

  const {
    getByColumn: baseServiceGetByColumn,
    put: baseServicePut,
    formErrors,
    setFormErrors,
  } = useBaseService(navigate);
  const { showLoader, hideLoader } = useLoader();
  const { showNotification } = useNotification();
  const { formData, handleChange, formatData, resetForm, setFormData } =
    useForm(setDefaultFieldValues(typeFields));

  const isKitOptions = useMemo(
    () => [
      { value: true, label: "Sim" },
      { value: false, label: "Não" },
    ],
    [],
  );

  const isKitSelect = useSelectOptions({
    fieldId: "type.is_kit",
    formData,
    rawOptions: isKitOptions,
    isMulti: false,
    mapToOption: (item) => ({ value: item.value, label: item.label }),
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const selects = {
    "type.is_kit": isKitSelect,
  };

  useEffect(() => {
    setFormData((prev) => ({
      type: {
        ...prev.type,
        is_kit: { value: true, label: "Sim" },
      },
    }));
  }, [setFormData]);

  const getSelectMeta = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId];
      if (!selectConfig) return {};

      return {
        hasNextPage: selectConfig.hasNextPage,
        isFetchingNextPage: selectConfig.isFetchingNextPage,
        isLoading: selectConfig.isLoading,
        isFetching: selectConfig.isFetching,
        handleMenuScrollToBottom: selectConfig.handleMenuScrollToBottom,
        handleSearch: selectConfig.handleSearch,
        onFocus: selectConfig.activateQuery,
      };
    },
    [selects],
  );

  // --- Funções para Selects (getOptions e getSelectedValue) ---
  const getOptions = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId];
      return selectConfig?.options || [];
    },
    [selects],
  );

  const getSelectedValue = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId];
      return selectConfig?.selectedValue || null;
    },
    [selects],
  );

  // --- Query para buscar os detalhes do Tipo ---
  const {
    data: typeData,
    isLoading: isLoadingType,
    isError: isErrorType,
    error: typeQueryError,
  } = useQueryWithLoader({
    queryKey: ["type", id],
    queryFn: async () => {
      showLoader();
      const response = await baseServiceGetByColumn(entities.types.getById(id));
      return response.result;
    },
    enabled: !!id,
    select: (data) => ({
      id: data.id,
      name: data.name,
      is_kit: {
        value: data.is_kit ? 1 : 0,
        label: data.is_kit ? "Sim" : "Não",
      },
    }),
    staleTime: 0,
  });

  useEffect(() => {
    if (!isLoadingType && !isErrorType && !typeQueryError && typeData) {
      setFormData({ type: typeData });
      hideLoader();
    }
    if (isErrorType || typeQueryError) {
      showNotification(
        "error",
        `Erro ao carregar tipo: ${
          typeQueryError.response?.data?.message || typeQueryError.message
        }`,
      );
      hideLoader();
      navigate(`/almoxarifado/produtos/tipos`, {
        replace: true,
        state: {
          type: "error",
          message: "Tipo não encontrado ou erro ao carregar.",
        },
      });
    }
  }, [
    isLoadingType,
    isErrorType,
    typeQueryError,
    typeData,
    formatData,
    hideLoader,
    showNotification,
    navigate,
    setFormData,
  ]);

  // --- Mutação para atualizar o Tipo ---
  const updateTypeMutation = useMutation({
    mutationFn: async (dataToUpdate) => {
      showLoader();
      setFormErrors({});
      const response = await baseServicePut(
        entities.types.update(id),
        dataToUpdate,
      );
      return response;
    },
    onSuccess: (response) => {
      if (response?.status === 200) {
        hideLoader();
        resetForm();
        queryClient.invalidateQueries({
          queryKey: ["almox_types", filters, pagination],
        });
        navigate(`/almoxarifado/produtos/tipos`, {
          state: { type: "success" },
        });
      }
    },
    onError: (error) => {
      console.log("onError ", error);
      if (error.response?.data?.errors) {
        setFormErrors(error.response.data.errors);
      }
      hideLoader();
    },
    onSettled: () => {
      hideLoader();
    },
  });

  const handleSubmit = useCallback(async () => {
    try {
      const cleanedData = cleanPayload(formData);
      const payload = {
        type: {
          ...cleanedData.type,
        },
      };
      await updateTypeMutation.mutateAsync(payload);
    } catch (error) {
      console.error("Erro ao enviar tipo:", {
        status: error.status,
        message: error.message,
      });
    }
  }, [formData, updateTypeMutation]);

  const handleBack = useCallback(() => {
    navigate("/almoxarifado/produtos/tipos");
  }, [navigate]);

  // useEffect para mensagens de estado de localização (mantido)
  useEffect(() => {
    if (location.state?.message) {
      showNotification(location.state.type, location.state.message);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, showNotification, navigate, location.pathname]);

  const renderForm = () => {
    const renderedSections = new Set();

    return typeFields.map((section, index) => {
      const sectionType = section.section.split(" - ")[0];
      const showHeader = !renderedSections.has(sectionType);

      if (showHeader) {
        renderedSections.add(sectionType);
      }

      return (
        <div key={section.section + " " + index}>
          {showHeader && (
            <SectionHeader
              section={section.section}
              viewTable={false}
              setViewTable={() => {}}
              addFieldsInData={() => {}}
            />
          )}

          <SimpleBody
            fields={section.fields}
            formErrors={normalizeFormErrors(formErrors)}
            formData={formData}
            handleFieldChange={handleChange}
            getOptions={getOptions}
            getSelectedValue={getSelectedValue}
            getSelectMeta={getSelectMeta}
          />
        </div>
      );
    });
  };

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Edição de Tipos"
        showBackButton={true}
        backUrl="/almoxarifado/produtos/tipos"
      />
      <div className="container-fluid p-1">
        <Form
          initialFormData={formData}
          onSubmit={handleSubmit}
          textSubmit="Salvar Alterações"
          textLoadingSubmit="Salvando..."
          handleBack={handleBack}
          isLoading={isLoadingType || updateTypeMutation.isPending}>
          {renderForm}
        </Form>
      </div>
    </MainLayout>
  );
};

export default EditTypePage;
